// cspell:ignore uncacheable
import type { GlyphInfo, GlyphRenderInstruction, TextEngine } from '@meitu/x-design-text';
import type { TextRenderInput, TextRenderSpanRange } from '../../types';
import { findSpanIndex } from '../../utils/snapshot';

/**
 * 编辑器实例级字形模板缓存的近似权重上限。
 *
 * 权重按模板 JSON 字符数乘 2 估算，只用于约束字符串载荷规模，不代表真实 JS heap 占用。
 */
const MAX_WEIGHT = 8 * 1024 * 1024;

type PathInstruction = Extract<GlyphRenderInstruction, { kind: 'path' }>;
type MissingInstruction = Extract<GlyphRenderInstruction, { kind: 'missing' }>;

/**
 * 可跨 layout 复用的位置无关绘制模板。
 *
 * path 保留轮廓、方向、fill rule 和合成粗斜体等模板事实，排除会随本轮排版变化的 glyphKey、坐标、
 * 颜色和 cluster 文本；skip/missing 同样在重建时补入当前位置。delegated 依赖浏览器宿主测量，使用
 * sentinel 明确阻止缓存路径发布，而不是猜测其绘制指令。
 */
type GlyphTemplate =
    | (Omit<PathInstruction, 'glyphKey' | 'x' | 'y' | 'fillRgba' | 'text'> & { kind: 'path' })
    | { kind: 'skip' }
    | (Omit<MissingInstruction, 'glyphKey' | 'x' | 'y'> & { kind: 'missing' })
    | { kind: 'uncacheable' };

/**
 * LRU 中保存的模板及其近似字符串权重。
 */
type CacheEntry = { template: GlyphTemplate; weight: number };

/**
 * 判断当前输入是否包含由 Engine 按整段结果生成的装饰线。
 *
 * decorationRect 不和 glyphInfos 一一对应，其几何还会随本轮 layout 改变，因此包含下划线或删除线时
 * 整轮绕过模板重建，直接读取 Engine 的完整 instructions。
 *
 * @param input - 当前完整 Text Render 输入。
 * @returns 存在任意全局或 span 装饰线时返回 true。
 */
const hasDecoration = (input: TextRenderInput): boolean =>
    input.underline || input.lineThrough || input.spans.some((span) => span.underline || span.lineThrough);

/**
 * 按 glyph 的 UTF-8 起始偏移读取实际生效的富文本样式。
 *
 * `styles` 与 `ranges` 都已经排除空 span，因此可以共享索引；没有命中显式 span 时回退到输入默认样式。
 *
 * @param input - 当前完整 Text Render 输入，兼作默认样式。
 * @param styles - 当前非空富文本 spans。
 * @param ranges - spans 在完整 UTF-8 文本中的范围。
 * @param byteStart - glyph cluster 的 UTF-8 起始偏移。
 * @returns 决定该 glyph 字体模板身份的样式。
 */
const resolveGlyphStyle = (
    input: TextRenderInput,
    styles: ReadonlyArray<TextRenderInput['spans'][number]>,
    ranges: readonly TextRenderSpanRange[],
    byteStart: number,
) => {
    const index = findSpanIndex(ranges, byteStart);
    return styles[index] ?? input;
};

/**
 * 创建位置无关的字形模板身份。
 *
 * 不使用 Engine 的 `glyphKey`：该值包含 x/y 亚像素位置，经 WASM 暴露为 JS number 时也不是无碰撞的
 * 语义身份。font revision 固定当前字体注册与 fallback 状态，其余字段描述 shaping 已确认的字体、
 * glyph、cluster 和样式；坐标、颜色、行高、字距、对齐与容器尺寸不会改变轮廓模板，故不进入 key。
 *
 * @param revision - 当前 Engine 字体状态版本。
 * @param input - 当前完整 Text Render 输入。
 * @param styles - 当前非空富文本 spans。
 * @param ranges - spans 在完整 UTF-8 文本中的范围。
 * @param glyph - 本轮 Engine 返回的 glyph placement。
 * @returns 可在当前字体状态下稳定复用的模板 key。
 */
const createKey = (
    revision: number,
    input: TextRenderInput,
    styles: ReadonlyArray<TextRenderInput['spans'][number]>,
    ranges: readonly TextRenderSpanRange[],
    glyph: GlyphInfo,
): string => {
    const style = resolveGlyphStyle(input, styles, ranges, glyph.start);
    return JSON.stringify([
        revision,
        style.fontFamily,
        glyph.glyphId,
        glyph.clusterText,
        style.fontSize,
        style.bold,
        style.italic,
        input.writingMode,
    ]);
};

/**
 * 从 Engine 绘制指令提取位置无关模板。
 *
 * 当前只缓存生产路径已经验证的 path、skip 和 missing。delegated 写入 uncacheable sentinel，使后续
 * rebuild 明确整轮回退；decorationRect 及未来新增的未知 kind 返回 null，使本轮 learn 完全放弃提交。
 *
 * @param instruction - Engine 本轮生成的绘制指令。
 * @returns 可缓存模板、uncacheable sentinel，或不支持缓存时的 null。
 */
const createTemplate = (instruction: GlyphRenderInstruction): GlyphTemplate | null => {
    if (instruction.kind === 'path') {
        const { glyphKey: _glyphKey, x: _x, y: _y, fillRgba: _fillRgba, text: _text, ...template } = instruction;
        return template;
    }
    if (instruction.kind === 'skip') return { kind: 'skip' };
    if (instruction.kind === 'missing') {
        const { glyphKey: _glyphKey, x: _x, y: _y, ...template } = instruction;
        return template;
    }
    if (instruction.kind === 'delegated') return { kind: 'uncacheable' };
    return null;
};

/**
 * 使用缓存模板和本轮 glyph placement 重建完整绘制指令。
 *
 * 模板提供轮廓与位置无关样式，本轮 GlyphInfo 提供 glyphKey、坐标、颜色和 cluster 文本。遇到
 * uncacheable sentinel 返回 null，由上层放弃整轮重建，不能把缓存指令与 Engine 新指令混合发布。
 *
 * @param template - 当前 key 命中的位置无关模板。
 * @param glyph - 本轮 layout 返回的 glyph placement。
 * @returns 重建后的完整指令；模板不可缓存时返回 null。
 */
const rebuildInstruction = (template: GlyphTemplate, glyph: GlyphInfo): GlyphRenderInstruction | null => {
    if (template.kind === 'uncacheable') return null;
    if (template.kind === 'path') {
        return {
            ...template,
            glyphKey: glyph.glyphKey,
            x: glyph.x,
            y: glyph.y,
            fillRgba: glyph.fillRgba,
            text: glyph.clusterText,
        };
    }
    if (template.kind === 'skip') {
        return { kind: 'skip', glyphKey: glyph.glyphKey, x: glyph.x, y: glyph.y };
    }
    return { ...template, glyphKey: glyph.glyphKey, x: glyph.x, y: glyph.y };
};

/**
 * 编辑器实例内共享的位置无关字形模板 LRU。
 *
 * 每个 Renderer 保留自己的 TextBuffer 和 snapshot，但相同编辑器中的多个文字图层可以复用相同字体
 * 轮廓。缓存只在一整轮 glyphs 全部命中并成功重建时返回 instructions；任一 miss、delegated、
 * decoration 或映射异常都会让调用方整轮读取 Engine，保证缓存路径与完整路径不会混合发布。
 *
 * Engine identity 或 font revision 变化会清空全部模板。该失效策略粒度较粗，但能在动态注册字体或
 * 修改 fallback 后保证旧轮廓不会继续复用。
 */
export class GlyphCache {
    /**
     * Map 的插入顺序表示 LRU 顺序：最旧条目在首部，最近命中或写入的条目在尾部。
     */
    private readonly entries = new Map<string, CacheEntry>();

    /**
     * 当前模板所属的 Engine 实例；null 表示尚未同步。
     */
    private engine: TextEngine | null = null;

    /**
     * 当前模板所属的 Engine 字体状态版本。
     */
    private revision = -1;

    /**
     * 全部缓存条目的近似字符串权重总和。
     */
    private weight = 0;

    /**
     * 将缓存切换到指定 Engine 字体状态。
     *
     * Engine 对象与 revision 都相同时保持缓存；任一变化都先清空旧模板，再记录新身份。Pipeline 在每次
     * 非空 layout 前调用本方法，因此 rebuild/learn 始终工作在明确的字体状态下。
     *
     * @param engine - 当前 ready 且未释放的 Engine。
     * @param revision - 当前 Engine 字体状态版本。
     * @returns 无返回值。
     */
    sync(engine: TextEngine, revision: number): void {
        if (this.engine === engine && this.revision === revision) return;
        this.clear();
        this.engine = engine;
        this.revision = revision;
    }

    /**
     * 尝试仅用本轮 GlyphInfo 和缓存模板重建整轮绘制指令。
     *
     * 只有无装饰线、glyph 非空且每个模板都命中并可重建时才返回结果。命中时通过 delete + set 把条目
     * 移到 Map 尾部，更新 LRU 新鲜度；任何一步失败立即返回 null，由调用方统一读取完整 Engine 指令。
     *
     * @param input - 当前完整 Text Render 输入。
     * @param ranges - 本轮非空 spans 的 UTF-8 范围。
     * @param glyphs - 本轮 layout 返回的有序 GlyphInfo。
     * @returns 完整重建的有序指令；无法安全整轮重建时返回 null。
     */
    rebuild(
        input: TextRenderInput,
        ranges: readonly TextRenderSpanRange[],
        glyphs: readonly GlyphInfo[],
    ): GlyphRenderInstruction[] | null {
        if (hasDecoration(input) || glyphs.length === 0) return null;
        const styles = input.spans.filter((span) => span.text.length > 0);
        const instructions: GlyphRenderInstruction[] = [];
        for (const glyph of glyphs) {
            const key = createKey(this.revision, input, styles, ranges, glyph);
            const entry = this.entries.get(key);
            if (!entry) return null;
            this.entries.delete(key);
            this.entries.set(key, entry);
            const instruction = rebuildInstruction(entry.template, glyph);
            if (!instruction) return null;
            instructions.push(instruction);
        }
        return instructions;
    }

    /**
     * 校验 Engine 的完整输出并学习本轮位置无关模板。
     *
     * decorationRect 不消费 glyph；其他指令必须和 GlyphInfo 按顺序一一对应，并校验 glyphKey、path
     * cluster 文本及 delegated byteStart。模板先写入 staged，只有全部指令映射成功且 glyph 数量完全
     * 一致后才批量提交，避免异常半轮污染共享缓存。delegated 会提交 sentinel，使未来相同输入明确
     * 回退完整路径；未知 instruction kind 会放弃整轮学习。
     *
     * @param input - 当前完整 Text Render 输入。
     * @param ranges - 本轮非空 spans 的 UTF-8 范围。
     * @param glyphs - 本轮 layout 返回的有序 GlyphInfo。
     * @param instructions - 同一 TextBuffer 返回的完整有序绘制指令。
     * @returns 无返回值；映射不完整或存在未知 kind 时不修改缓存。
     */
    learn(
        input: TextRenderInput,
        ranges: readonly TextRenderSpanRange[],
        glyphs: readonly GlyphInfo[],
        instructions: readonly GlyphRenderInstruction[],
    ): void {
        const staged: Array<{ key: string; template: GlyphTemplate }> = [];
        const styles = input.spans.filter((span) => span.text.length > 0);
        let glyphIndex = 0;

        for (const instruction of instructions) {
            if (instruction.kind === 'decorationRect') continue;
            const glyph = glyphs[glyphIndex++];

            if (!glyph) return;
            if ('glyphKey' in instruction && instruction.glyphKey !== glyph.glyphKey) return;
            if (instruction.kind === 'path' && instruction.text !== glyph.clusterText) return;
            if (instruction.kind === 'delegated' && instruction.byteStart !== glyph.start) return;

            const template = createTemplate(instruction);
            if (!template) return;

            staged.push({ key: createKey(this.revision, input, styles, ranges, glyph), template });
        }

        if (glyphIndex !== glyphs.length) return;
        staged.forEach(({ key, template }) => this.set(key, template));
    }

    /**
     * 清空全部模板及累计权重，但保留最近同步的 Engine 与 revision 身份。
     *
     * @returns 无返回值。
     */
    clear(): void {
        this.entries.clear();
        this.weight = 0;
    }

    /**
     * 返回当前模板 JSON 的近似字符串权重。
     *
     * 该值用于性能基准和 LRU 上限判断，不代表浏览器实际分配的内存字节数。
     */
    get estimatedWeight(): number {
        return this.weight;
    }

    /**
     * 写入或刷新一个模板，并从最旧条目开始淘汰直到回到权重上限内。
     *
     * 覆盖已有 key 时先扣除旧权重并移动到 Map 尾部。单个模板若自身超过上限，也会按同一规则被
     * 淘汰；缓存容量只影响命中率，不影响正确性。
     *
     * @param key - 位置无关模板身份。
     * @param template - 已通过整轮映射校验的模板。
     * @returns 无返回值。
     */
    private set(key: string, template: GlyphTemplate): void {
        const previous = this.entries.get(key);
        if (previous) this.weight -= previous.weight;

        const weight = JSON.stringify(template).length * 2;

        this.entries.delete(key);
        this.entries.set(key, { template, weight });
        this.weight += weight;

        while (this.weight > MAX_WEIGHT && this.entries.size > 0) {
            const oldestKey = this.entries.keys().next().value;
            if (oldestKey === undefined) break;
            const oldest = this.entries.get(oldestKey);
            this.entries.delete(oldestKey);
            this.weight -= oldest?.weight ?? 0;
        }
    }
}
